import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import { TimeLine, TimeLineItem } from "shared/components/TimeLine";
import { VerificationStatus, VerificationOverriddenBy } from "shared/types";
import { Check, XClose } from "untitledui-js-base";
import ArchiveSvg from "../assets/archive.svg?react";
import DocumentSvg from "../assets/document.svg?react";
import SelfieSvg from "../assets/face-recognition.svg?react";
import { cx } from "shared/utils";
import { ReactNode } from "react";
import { VerificationStatusAlert } from "./VerificationStatusAlert";

const Bullet = ({
  isSuccess,
  children
}: {
  isSuccess: boolean;
  children: ReactNode;
}) => {
  return (
    <div
      className={cx(
        "h-10 w-10  shrink-0 rounded-full relative border flex items-center justify-center -translate-x-[29px] border-alpha-100",
        isSuccess ? "bg-success-50" : "bg-danger-50"
      )}
    >
      <div
        className={cx(
          "absolute h-[18px] flex items-center justify-center w-[18px] border border-white rounded-full -left-1 -top-1",
          isSuccess ? "bg-success-600" : "bg-danger-600"
        )}
      >
        {isSuccess ? (
          <Check size="10px" className="stroke-white stroke-[2px]" />
        ) : (
          <XClose size="10px" className="stroke-white stroke-[2px]" />
        )}
      </div>

      <div className={isSuccess ? "text-success-600" : "text-danger-500"}>
        {children}
      </div>
    </div>
  );
};

const SummaryBadge = ({ status }: { status: VerificationStatus }) => {
  switch (status) {
    case VerificationStatus.Verified:
    case VerificationStatus.Finished:
      return (
        <Badge
          size={BadgeSize.BASE}
          variant={BadgeVariant.SUCCESS}
          className="items-center"
        >
          Passed
        </Badge>
      );
    case VerificationStatus.ManuallyApproved:
      return (
        <Badge
          size={BadgeSize.BASE}
          variant={BadgeVariant.SUCCESS}
          className="items-center"
        >
          Passed (Manually overridden)
        </Badge>
      );
    case VerificationStatus.FinishedWithWarnings:
      return (
        <Badge
          size={BadgeSize.BASE}
          variant={BadgeVariant.WARNING}
          className="items-center"
          leftElement={
            <div className="text-[6px] shrink-0 font-bold h-2.5 w-2.5 rounded-full bg-warning-500 flex items-center justify-center text-warning-50">
              !
            </div>
          }
        >
          Passed with warnings
        </Badge>
      );
    case VerificationStatus.Failed:
      return (
        <Badge
          size={BadgeSize.BASE}
          variant={BadgeVariant.ERROR}
          className="items-center"
        >
          Failed
        </Badge>
      );

    default:
      return null;
  }
};

export const Summary = ({
  fullName,
  verificationStatus,
  sessionId,
  ipAddress,
  isSelfieCheckSuccess,
  isDocumentCheckSuccess,
  isRecordCheckSuccess,
  isSelfieCheckEnabled,
  openOverrideIdentityModal,
  overriddenBy
}: {
  fullName: string;
  verificationStatus: VerificationStatus;
  sessionId: string;
  ipAddress: string;
  isSelfieCheckSuccess: boolean;
  isDocumentCheckSuccess: boolean;
  isRecordCheckSuccess: boolean;
  isSelfieCheckEnabled: boolean;
  openOverrideIdentityModal?: () => void;
  overriddenBy: VerificationOverriddenBy | null;
}) => {
  return (
    <>
      <VerificationStatusAlert
        verificationStatus={
          overriddenBy
            ? VerificationStatus.ManuallyApproved
            : verificationStatus
        }
        openOverrideIdentityModal={openOverrideIdentityModal}
        overriddenBy={overriddenBy}
      />

      <div className="flex gap-8 space-x-px md:divide-x-0 flex-wrap md:flex-nowrap">
        <div className="w-full p-6 idv-background rounded-lg border border-alpha-100 shadow-xs">
          <div className="space-y-2 mb-4 pb-4 border-b border-alpha-40">
            <SummaryBadge
              status={
                overriddenBy
                  ? VerificationStatus.ManuallyApproved
                  : verificationStatus
              }
            />
            <div className="flex gap-2 justify-between items-center mb-6">
              <h1 className="text-bold font-bold text-base text-content-1000 truncate">
                {fullName}
              </h1>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between w-full mb-2">
              <p className="text-xs font-medium text-content-300">Session ID</p>
              <p className="text-xs font-medium text-content-1000">
                {sessionId}
              </p>
            </div>

            <div className="flex justify-between w-full">
              <p className="text-xs font-medium text-content-300">IP address</p>
              <p className="text-xs font-medium text-content-1000">
                {ipAddress}
              </p>
            </div>
          </div>
        </div>

        <div className="md:w-px bg-alpha-40 h-[192px] hidden md:block" />

        <div className="w-full pl-3">
          <TimeLine wrapperClassName="pt-0 pl-2 !border-alpha-200">
            <TimeLineItem
              wrapperClassName="items-center mb-6"
              childrenClassName="text-sm text-content-1000 font-medium -ml-3"
              bullet={
                <Bullet isSuccess={isRecordCheckSuccess}>
                  <ArchiveSvg className="text-inherit" />
                </Bullet>
              }
            >
              Records match verification{" "}
              {isRecordCheckSuccess ? "passed" : "failed"}
            </TimeLineItem>
            <TimeLineItem
              wrapperClassName="items-center mb-6"
              childrenClassName="text-sm text-content-1000 font-medium -ml-3"
              bullet={
                <Bullet isSuccess={isDocumentCheckSuccess}>
                  <DocumentSvg className="text-inherit" />
                </Bullet>
              }
            >
              Document verification{" "}
              {isDocumentCheckSuccess ? "passed" : "failed"}
            </TimeLineItem>
            {isSelfieCheckEnabled ? (
              <TimeLineItem
                wrapperClassName="items-center"
                childrenClassName="text-sm text-content-1000 font-medium -ml-3"
                bullet={
                  <Bullet isSuccess={isSelfieCheckSuccess}>
                    <SelfieSvg className="text-inherit" />
                  </Bullet>
                }
              >
                Selfie check {isSelfieCheckSuccess ? "passed" : "failed"}
              </TimeLineItem>
            ) : null}
          </TimeLine>
        </div>
      </div>
    </>
  );
};
